import { defineFeature } from '../define-feature';
import {
  TEST_IDS,
  notificationChannelToggleTestId,
  notificationEventCardTestId,
  sidebarNavTestId,
  sidebarSubTestId,
} from '../test-ids';

export default defineFeature({
  id: 'marketing.messages.automatic',
  category: 'marketing',
  title: 'Gérer les messages automatiques',
  intent:
    'Activer, par canal (e-mail et/ou SMS), les messages automatiques envoyés à vos clients et à vous — confirmations, rappels, annulations, avis, cartes cadeaux, alertes — et les personnaliser selon votre formule.',
  context: {
    why: "Les messages automatiques sont la relation client qui tourne toute seule. Un rappel de rendez-vous envoyé la veille réduit fortement les oublis et les no-show ; une demande d'avis envoyée après la séance alimente votre réputation sans que vous y pensiez ; le suivi des cartes cadeaux (acheté, bientôt expiré, utilisé) tient vos clients informés automatiquement. Et certains de ces messages vous préviennent, vous : un nouveau rendez-vous, un quota qui baisse, un message de contact reçu. Tout est déjà réglé par défaut — vous n'avez rien à écrire pour que ça marche. Votre travail se limite à choisir, pour chaque message, s'il part par e-mail, par SMS, ou les deux, et à personnaliser les textes si votre formule le permet.",
    use_cases: [
      'Activer le rappel de rendez-vous par SMS pour réduire les no-show.',
      "Envoyer automatiquement une demande d'avis après chaque rendez-vous.",
      "Être alerté d'un quota faible ou d'un nouveau message de contact.",
    ],
  },
  required_scopes: ['notification.update'],
  ui_entry_points: [{ route: '/marketing/notifications', element: 'Écran « Messages automatiques »' }],
  api: {
    primary_operation_id: 'NotificationPreferenceAppController_upsert',
    related_operation_ids: ['NotificationPreferenceAppController_list'],
  },
  steps: [
    '**Ouvrez « Messages automatiques »** depuis Communication.',
    '**Parcourez les messages par défaut**, regroupés par thème (rendez-vous, avis, cartes cadeaux, alertes).',
    '**Pour chaque message, choisissez le canal** : e-mail, SMS, ou les deux.',
    '**Personnalisez le contenu** si votre formule le permet.',
    "**Enregistrez** : vos réglages s'appliquent aux prochains envois.",
  ],
  gotchas: [
    "**Une base par défaut existe déjà : tout fonctionne sans réglage.** Vous n'avez rien à créer pour que les confirmations et rappels partent. Vous ne faites qu'ajuster les canaux et, éventuellement, les textes.",
    "**Les messages couvrent quatre grands thèmes.** Les rendez-vous (confirmation, en attente, rappel au client, rappel à vous avant chaque RDV, modification, annulation par le client ou par vous, nouveau rendez-vous côté pro) ; les avis (demande d'avis, nouvel avis) ; les cartes cadeaux (acheté, offert, bientôt expiré, utilisé, rechargé, restitué, annulé) ; et les alertes qui vous sont destinées (quota faible, nouveau message de contact).",
    "**La personnalisation des textes dépend de votre formule.** L'activation des canaux (e-mail / SMS) reste accessible à tous ; réécrire le contenu des messages est réservé à la formule supérieure.",
    "**Les SMS de ces messages sont facturés, comme les campagnes.** Activer le SMS sur un message très fréquent (un rappel, par exemple) a un coût récurrent : arbitrez selon l'intérêt de chaque message.",
    "**Le nom d'expéditeur vient des Paramètres de communication.** Réglez-le une fois là-bas ; il s'applique à tous ces messages automatiques.",
  ],
  related_features: [
    'marketing.communication.settings',
    'agenda.booking.settings',
    'sales.voucher.manage',
    'catalog.pack.manage',
  ],
  demo_script: {
    duration_target_s: 35,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Les messages automatiques entretiennent la relation client toute seule — rappels, avis, cartes cadeaux.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      {
        action: 'click',
        target: sidebarSubTestId('/marketing/notifications'),
        caption: 'Puis « Messages automatiques ».',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.messages_auto_list,
        caption: 'La liste est prête ; tout fonctionne déjà par défaut, vous n’avez rien à écrire.',
      },
      {
        action: 'click',
        target: notificationEventCardTestId('BOOKING_WAITING_CONFIRMATION'),
        caption: 'Ouvrez un message, ici le rendez-vous en attente de confirmation.',
        capture: true,
      },
      {
        // Le bouton « Enregistrer » de cette modale est rendu deux fois (mobile +
        // desktop) sans cible unique → on s'arrête à l'activation du canal.
        action: 'click',
        target: notificationChannelToggleTestId('EMAIL'),
        caption: 'Choisissez son canal : ici on active l’e-mail, et le message partira tout seul le moment venu.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Messages automatiques : tous les transactionnels par défaut — confirmations de
// > RDV, rappels, annulations (pro/client), modifications, nouveau RDV, RDV annulés, collecte d'avis ;
// > cartes cadeaux (acheté, reçu, bientôt expiré, utilisé, rechargé) ; activité paiement (quota faible,
// > paiement reçu, nouveau message). Activation par e-mail et/ou SMS. Base par défaut ; personnalisation
// > réservée à la formule supérieure.
